import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, Button, Space, Typography } from 'antd';
import { 
  ApiOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ReloadOutlined,
  PrinterOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { getLIMSStatistics } from '../../services/limsService';
import { useReactToPrint } from 'react-to-print';

const { Title, Text } = Typography;

const LIMSDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef();

  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getLIMSStatistics();
      setStatistics(data);
    } catch (err) {
      setError('Failed to load LIMS statistics');
      console.error('Error loading LIMS statistics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: 'LIMS Integration Dashboard Report',
    pageStyle: `
      @media print {
        body { -webkit-print-color-adjust: exact; }
        .ant-statistic { break-inside: avoid; }
        .ant-card { break-inside: avoid; margin-bottom: 16px; }
        .no-print { display: none !important; }
      }
    `
  });

  const handleExportExcel = () => {
    if (!statistics) return;
    
    const data = [
      ['LIMS Integration Statistics Report'],
      ['Generated on:', new Date().toLocaleString()],
      [''],
      ['Metric', 'Value'],
      ['Total API Tokens', statistics.total_tokens || 0],
      ['Active API Tokens', statistics.active_tokens || 0],
      ['Total Sample Confirmations', statistics.total_confirmations || 0],
      ['Confirmed Samples', statistics.confirmed_samples || 0],
      ['Rejected Samples', statistics.rejected_samples || 0],
      ['Pending Samples', statistics.pending_samples || 0],
      ['Total Integration Logs', statistics.total_logs || 0],
      ['Successful Logs', statistics.successful_logs || 0],
      ['Failed Logs', statistics.failed_logs || 0],
      ['Pending Logs', statistics.pending_logs || 0],
      [''],
      ['Recent Activity'],
      ['Type', 'Status', 'Timestamp']
    ];

    // Add recent activity data
    if (statistics.recent_activity && statistics.recent_activity.length > 0) {
      statistics.recent_activity.forEach(activity => {
        data.push([
          activity.integration_type || 'Sample Confirmation',
          activity.status || 'Unknown',
          new Date(activity.timestamp || activity.created_at).toLocaleString()
        ]);
      });
    }

    // Convert to CSV and download
    const csvContent = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `lims_dashboard_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading LIMS statistics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadStatistics}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div ref={printRef}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>LIMS Integration Dashboard</Title>
        <Space className="no-print">
          <Button 
            icon={<PrinterOutlined />} 
            onClick={handlePrint}
            type="primary"
          >
            Print Report
          </Button>
          <Button 
            icon={<FileExcelOutlined />} 
            onClick={handleExportExcel}
            disabled={!statistics}
          >
            Export Excel
          </Button>
          <Button icon={<ReloadOutlined />} onClick={loadStatistics}>
            Refresh
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {/* API Tokens Statistics */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="API Tokens"
              value={statistics?.total_tokens || 0}
              prefix={<ApiOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <Text type="secondary">
              {statistics?.active_tokens || 0} active
            </Text>
          </Card>
        </Col>

        {/* Sample Confirmations Statistics */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Sample Confirmations"
              value={statistics?.total_confirmations || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="success">
                {statistics?.confirmed_samples || 0} confirmed
              </Text>
              <br />
              <Text type="danger">
                {statistics?.rejected_samples || 0} rejected
              </Text>
              <br />
              <Text type="warning">
                {statistics?.pending_samples || 0} pending
              </Text>
            </div>
          </Card>
        </Col>

        {/* Integration Logs Statistics */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Integration Logs"
              value={statistics?.total_logs || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="success">
                {statistics?.successful_logs || 0} successful
              </Text>
              <br />
              <Text type="danger">
                {statistics?.failed_logs || 0} failed
              </Text>
              <br />
              <Text type="warning">
                {statistics?.pending_logs || 0} pending
              </Text>
            </div>
          </Card>
        </Col>

        {/* System Status */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="System Status"
              value="Operational"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary">
              All systems running
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Recent Activity */}
      {statistics?.recent_activity && statistics.recent_activity.length > 0 && (
        <Card title="Recent Activity" style={{ marginTop: '24px' }}>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {statistics.recent_activity.map((activity, index) => (
              <div key={index} style={{ 
                padding: '8px 0', 
                borderBottom: index < statistics.recent_activity.length - 1 ? '1px solid #f0f0f0' : 'none' 
              }}>
                <Space>
                  <Text strong>{activity.integration_type || 'Sample Confirmation'}</Text>
                  <Text type={activity.status === 'SUCCESS' || activity.status === 'CONFIRMED' ? 'success' : 'danger'}>
                    {activity.status}
                  </Text>
                  <Text type="secondary">
                    {new Date(activity.timestamp || activity.created_at).toLocaleString()}
                  </Text>
                </Space>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default LIMSDashboard;
